import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthPage from "@/pages/AuthPage";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Journal from "@/pages/Journal";
import Stats from "@/pages/Stats";
import GlobalStats from "@/pages/GlobalStats";
import Settings from "@/pages/Settings";
import TradeDetail from "@/pages/TradeDetail";
import AdminUsers from "@/pages/AdminUsers";
import NotificationTemplates from "@/pages/NotificationTemplates";
import AcceptInvite from "@/pages/AcceptInvite";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Help from "@/pages/Help";
import { isAtLeast, ROLES } from "@/lib/permissions";
import { Toaster } from "@/components/ui/sonner";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAtLeast(user, ROLES.ADMIN)) return <Navigate to="/journal" replace />;
  return children;
}

function SuperAdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAtLeast(user, ROLES.SUPER_ADMIN)) return <Navigate to="/journal" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/journal" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Navigate to="/journal" replace />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/stats" element={<AdminOnly><Stats /></AdminOnly>} />
            <Route path="/global-stats" element={<GlobalStats />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/help" element={<Help />} />
            <Route path="/admin/users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
            <Route path="/admin/notification-templates" element={<SuperAdminOnly><NotificationTemplates /></SuperAdminOnly>} />
            <Route path="/trades/:id" element={<TradeDetail />} />
            <Route path="*" element={<Navigate to="/journal" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
